import {
	createRootRouteWithContext,
	createRoute,
	createRouter,
	type AnyRoute,
	type AnyRootRoute,
	type RouteComponent,
} from '@tanstack/react-router';

import config from '@/config';
import { basepath } from '@/constants';
import { NotFound, RootRouteComponent } from './root-route';
import { RoutePending, RouteError } from './routing/route-boundaries';
import {
	createRouterContext,
	type RouterContext,
} from './routing/router-context';

import {
	buildRouteCatalog,
	type RouteCatalogKey,
	type RouteProtectionMap,
} from './routing/route-catalog';
import {
	resolveProtection,
	type RouteProtectionSpec,
	type TemplateAuthWiring,
} from './routing/route-protection';

type CreateRouteOptions = Parameters<typeof createRoute>[0];

export interface RouteSpec {
	path: string;
	component: RouteComponent;
	validateSearch?: CreateRouteOptions['validateSearch'];
	/**
	 * Structural, not TanStack's own per-route-generic `beforeLoad` type --
	 * that type is parented to a *specific* route's generics and doesn't
	 * match across the different parent routes this manifest field needs to
	 * cover. Shaped to match `route-protection.ts`'s `RouteGuardContext` so
	 * `poets`/`morningstar`'s home guard (`token-auth-guard.ts`'s
	 * `tokenAuthGuard`) can be passed here directly.
	 */
	beforeLoad?: (opts: {
		params: Record<string, string>;
		search: unknown;
		location: { pathname: string };
	}) => unknown;
	/**
	 * Same `'auth'` opt-in the catalog routes get. Prefer this over a
	 * hand-written `beforeLoad` so the home route follows the tenant's auth
	 * config too.
	 */
	protect?: RouteProtectionSpec;
	/**
	 * Topic 08's loader item, home's slice of it. Same looseness as
	 * `beforeLoad` above and for the same reason -- TanStack's own
	 * per-route-generic `loader` type is parented to a specific route's
	 * generics, which this shared manifest field can't match across every
	 * template's home route. Only wired for templates whose home page
	 * already fetches through a `shared/api/queries.ts` `*Options` factory
	 * (`issuesOptions`/`articlesOptions` via `@shared/lib/hooks`'s
	 * `useIssues`/`useArticles`) -- `daily`/`starmagazine`/`toyota` keep
	 * their own bespoke, unmigrated home fetch (see `03-data-layer-tanstack-
	 * query.md`'s "Migrate view hooks" item) and don't get one here.
	 */
	loader?: (opts: {
		context: RouterContext;
		params: Record<string, string>;
	}) => unknown;
}

export interface TenantAppManifest {
	home: RouteSpec;
	/** Catalog route names this template opts into (see routing/routeCatalog.tsx). */
	routes?: RouteCatalogKey[];
	/**
	 * Which routes this template wants protected. Usually `'auth'` -- "guard
	 * this with whatever this tenant authenticates with" -- so the same
	 * template serves a Cognito tenant and a token-auth tenant without
	 * re-declaring the route. See routing/route-protection.ts.
	 */
	protect?: Partial<Record<RouteCatalogKey, RouteProtectionSpec>>;
	/**
	 * The auth mechanisms this template can wire up. Offering one doesn't turn
	 * it on: `config.app.auth.type` picks which is used, so a `basic` build
	 * deployed for a token-auth tenant runs unguarded even though it ships
	 * Cognito wiring.
	 */
	auth?: TemplateAuthWiring;
	/**
	 * Template-exclusive routes -- the escape hatch for routes that don't
	 * exist in ≥2 templates (improvements/07-template-architecture.md:
	 * "not all templates have all the routes"). Each template's own
	 * App.router.tsx builds these with plain `createRoute` calls, exactly as
	 * before this factory existed, and hands the finished route objects here.
	 */
	custom?: (rootRoute: AnyRootRoute) => AnyRoute[];
	/** Renders `<SessionTracker />` inside the root route, like daily/starmagazine/toyota did by hand. Default false. */
	sessionTracker?: boolean;
}

/**
 * Shared "core app" factory (improvements/07-template-architecture.md step 3):
 * assembles a router from a template's manifest instead of every template
 * hand-rolling its own createRootRoute/createRoute/createRouter tree.
 *
 * `drafts` stays admin-gated and `favorite` stays favorite-gated here
 * exactly as they were in every per-template router before this factory
 * existed (`if (config?.app?.admin) routes.push(draftsRoute)` /
 * `if (config?.app?.favorite) routes.push(favoriteRoute)`).
 */
export function createTenantApp(manifest: TenantAppManifest) {
	let router: ReturnType<typeof createRouter>;

	const rootRoute = createRootRouteWithContext<RouterContext>()({
		component: () => (
			<RootRouteComponent sessionTracker={manifest.sessionTracker} />
		),
	});

	const protect: RouteProtectionMap = {};
	for (const [name, spec] of Object.entries(manifest.protect ?? {})) {
		const resolved = resolveProtection(spec, manifest.auth);
		if (resolved) protect[name as RouteCatalogKey] = resolved;
	}

	const catalog = buildRouteCatalog(rootRoute, protect);

	const homeProtection = resolveProtection(
		manifest.home.protect,
		manifest.auth,
	);

	const homeRoute = createRoute({
		getParentRoute: () => rootRoute,
		path: manifest.home.path,
		component: manifest.home.component,
		validateSearch: manifest.home.validateSearch,
		beforeLoad:
			manifest.home.beforeLoad ??
			(homeProtection
				? async (opts: {
						params: Record<string, string>;
						search: unknown;
						location: { pathname: string };
					}) => {
						await homeProtection.beforeLoad?.({
							params: opts.params,
							search: opts.search as Record<string, unknown>,
							pathname: opts.location.pathname,
						});
						return homeProtection.provide;
					}
				: undefined),
		loader: manifest.home.loader,
	});

	const routeNames = (manifest.routes ?? []).filter((name) => {
		if (name === 'drafts') return !!config?.app?.admin;
		if (name === 'favorite') return !!config?.app?.favorite;
		return true;
	});
	// `issuesLatest` (the redirect target for a manifest `shortcuts` entry --
	// see docs/wiki/Manifest-Shortcuts.md) is auto-included wherever `issue`
	// is, rather than requiring every template's own manifest to opt in by
	// hand: it's meaningless without `issue` to redirect into, and adds no
	// UI/bundle cost of its own wherever `issue` already exists. `padi`,
	// which doesn't list `issue` (its own separate routing/auth mechanism),
	// correctly never gets it.
	if (routeNames.includes('issue') && !routeNames.includes('issuesLatest')) {
		routeNames.push('issuesLatest');
	}
	const catalogRoutes = routeNames.map((name) => catalog[name]);
	const customRoutes = manifest.custom?.(rootRoute) ?? [];

	const routeTree = rootRoute.addChildren([
		homeRoute,
		...catalogRoutes,
		...customRoutes,
	]);

	router = createRouter({
		routeTree,
		basepath,
		// { queryClient, auth, config } -- see routing/router-context.ts.
		// Typed once here via createRootRouteWithContext above, so every
		// catalog route's beforeLoad/loader gets it typed for free.
		context: createRouterContext(),
		// Preload a route's code + loader data on hover/touchstart. The
		// stale-time is 0 on purpose: TanStack Query already owns staleness
		// (see shared/query-client's getQueryCacheOptions), so letting the
		// router keep its own 30s preload cache on top would mean two
		// different answers to "is this data fresh?".
		defaultPreload: 'intent',
		defaultPreloadStaleTime: 0,
		// "Back from an article dumps me at the top of the list" -- one line,
		// all ten templates.
		scrollRestoration: true,
		defaultPendingComponent: RoutePending,
		defaultErrorComponent: RouteError,
		defaultNotFoundComponent: NotFound,
	});

	return router;
}
